import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Chip from '@/components/ui/Chip';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import type { OracaoAdmin } from '../types';
import { normalizarOracoesAdmin, rotuloDiaCurto } from '../utils';
import FormOracao from './FormOracao';

function isoHoje(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function isoMais(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function textoRezaram(n: number): string {
  return n === 1 ? '1 rezou' : `${n} rezaram`;
}

function textoAceitaram(n: number): string {
  return n === 1 ? '1 aceitou' : `${n} aceitaram`;
}

// ---------------------------------------------------------------------------
// Aba "Orações" do painel da plataforma (só admin). Lista os dias, deixa
// editar/aprovar cada um e aprovar os próximos 7 dias de uma vez.
// Tudo é feito pelas funções do banco; aqui só mostramos e disparamos.
// ---------------------------------------------------------------------------
export default function Oracoes() {
  const [oracoes, setOracoes] = useState<OracaoAdmin[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [abertoDia, setAbertoDia] = useState<string | null>(null);
  const [confirmar7, setConfirmar7] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [erroAprovar, setErroAprovar] = useState<string | null>(null);

  const carregar = useCallback(async (inicial = false) => {
    if (inicial) setCarregando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('oracoes_admin_listar');
      if (error) throw error;
      const lista = normalizarOracoesAdmin(data);
      lista.sort((a, b) => a.dia.localeCompare(b.dia));
      setOracoes(lista);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      if (inicial) setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar(true);
  }, [carregar]);

  const hoje = useMemo(() => isoHoje(), []);
  const limite = useMemo(() => isoMais(6), []);

  const proximos = useMemo(
    () => oracoes.filter((o) => o.dia >= hoje && o.dia <= limite && !o.aprovada),
    [oracoes, hoje, limite],
  );

  const itemAberto = useMemo(
    () => oracoes.find((o) => o.dia === abertoDia) ?? null,
    [oracoes, abertoDia],
  );

  async function aprovar7() {
    setAprovando(true);
    setErroAprovar(null);
    try {
      const dias = proximos.map((o) => o.dia);
      const { data, error } = await supabase.rpc('oracoes_admin_aprovar', {
        p_dias: dias,
        p_aprovar: true,
      });
      if (error) throw error;
      const r = (data ?? {}) as { alteradas?: number };
      const n = Number(r.alteradas) || 0;
      setConfirmar7(false);
      mostrarToast(n === 1 ? '1 aprovada' : `${n} aprovadas`, 4000, 'sucesso');
      await carregar();
    } catch (e) {
      setErroAprovar(mensagemAmigavel(e));
    } finally {
      setAprovando(false);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-3">
        <div className="h-12 animate-pulse rounded-[18px] bg-background-200/70" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-[18px] bg-background-200/70" />
        ))}
      </div>
    );
  }

  if (erro && oracoes.length === 0) {
    return (
      <Cartao className="p-6 text-center">
        <p className="text-base font-semibold text-foreground-950">Não foi possível carregar</p>
        <p className="mt-1 text-sm text-foreground-600">{erro}</p>
        <div className="mt-5 flex justify-center">
          <Botao variante="primario" icone="ri-refresh-line" onClick={() => void carregar(true)}>
            Tentar de novo
          </Botao>
        </div>
      </Cartao>
    );
  }

  return (
    <div className="space-y-5">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          {oracoes.length} {oracoes.length === 1 ? 'dia' : 'dias'} na lista
        </p>
        <Botao
          variante="primario"
          icone="ri-check-double-line"
          disabled={proximos.length === 0}
          onClick={() => {
            setErroAprovar(null);
            setConfirmar7(true);
          }}
        >
          Aprovar os próximos 7 dias
        </Botao>
      </section>

      {oracoes.length === 0 ? (
        <EmptyState
          icone="ri-book-open-line"
          titulo="Nenhuma oração ainda"
          descricao="Quando os dias forem preparados, eles aparecem aqui."
        />
      ) : (
        <section className="space-y-3">
          {oracoes.map((o) => (
            <Cartao key={o.dia} className="overflow-hidden">
              <button
                type="button"
                onClick={() => setAbertoDia(o.dia)}
                className="aperta flex w-full cursor-pointer items-start justify-between gap-3 p-4 text-left transition-colors hover:bg-background-50"
              >
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground-950">
                    {rotuloDiaCurto(o.dia)}
                    {o.imagem_url && (
                      <i className="ri-image-line text-foreground-400" aria-hidden />
                    )}
                    {o.video_url && (
                      <i className="ri-video-line text-foreground-400" aria-hidden />
                    )}
                  </p>
                  {o.santo && (
                    <p className="mt-0.5 truncate text-sm font-medium text-foreground-800">
                      {o.santo}
                    </p>
                  )}
                  <p className="mt-0.5 truncate text-sm text-foreground-600">
                    {o.tema || 'Sem tema'}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {o.evangelho_ref || 'Sem referência'}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <Chip tipo={o.aprovada ? 'success' : 'warning'}>
                    {o.aprovada ? 'Aprovada' : 'Rascunho'}
                  </Chip>
                  <span className="text-xs text-foreground-500">
                    {textoRezaram(o.rezaram)} · {textoAceitaram(o.aceitaram)}
                  </span>
                </div>
              </button>
            </Cartao>
          ))}
        </section>
      )}

      <FormOracao
        item={itemAberto}
        aberto={Boolean(abertoDia)}
        aoFechar={() => setAbertoDia(null)}
        aoAtualizar={() => void carregar()}
      />

      <Modal
        aberto={confirmar7}
        titulo="Aprovar os próximos 7 dias"
        aoFechar={() => {
          if (!aprovando) setConfirmar7(false);
        }}
        rodape={
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Botao variante="secundario" disabled={aprovando} onClick={() => setConfirmar7(false)}>
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              icone="ri-check-line"
              carregando={aprovando}
              onClick={() => void aprovar7()}
            >
              Aprovar {proximos.length}
            </Botao>
          </div>
        }
      >
        <p className="text-sm text-foreground-700">
          Aprovadas saem no aviso das 6h de cada dia.
        </p>
        <ul className="mt-3 divide-y divide-background-200">
          {proximos.map((o) => (
            <li key={o.dia} className="flex items-center justify-between gap-3 py-2.5">
              <span className="shrink-0 text-sm font-medium text-foreground-900">
                {rotuloDiaCurto(o.dia)}
              </span>
              <span className="min-w-0 truncate text-right text-sm text-foreground-600">
                {o.tema || 'Sem tema'}
              </span>
            </li>
          ))}
        </ul>
        {erroAprovar && (
          <p className="mt-3 flex items-start gap-1.5 text-sm text-danger-600" role="alert">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" aria-hidden />
            </span>
            <span className="min-w-0">{erroAprovar}</span>
          </p>
        )}
      </Modal>
    </div>
  );
}